import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useProject } from '../context/ProjectContext';
import { getApiUrl, axiosConfig } from '../config/api';
import UpgradeModal from '../components/UpgradeModal';
import './CompetitorAnalysis.css';
import '../components/SEOAnalyzer.css';

// Fun facts for loading overlays (same as Dashboard)
const funFacts = [
  "Did you know? Google processes over 8.5 billion searches per day!",
  "Did you know? 75% of users never scroll past the first page of search results.",
  "Did you know? Websites that load in 2 seconds have a 9% bounce rate, while those taking 5 seconds have a 38% bounce rate.",
  "Did you know? Mobile-first indexing means Google primarily uses the mobile version of your site for ranking.",
  "Did you know? Pages with images get 94% more views than those without.",
  "Did you know? The average user reads only 20% of the text on a webpage.",
  "Did you know? Websites with SSL certificates (HTTPS) rank higher in Google search results.",
  "Did you know? The first 3 seconds of page load time impact 53% of mobile site visits.",
  "Did you know? Internal linking can improve your site's SEO and help users navigate better.",
  "Did you know? Long-form content (2000+ words) tends to rank higher in search results.",
  "Did you know? Schema markup can help your website appear in rich snippets, increasing click-through rates.",
  "Did you know? Core Web Vitals are now a ranking factor for Google search results.",
  "Did you know? Websites with fast loading times have better conversion rates.",
  "Did you know? Alt text for images not only helps SEO but also improves accessibility.",
  "Did you know? A well-structured sitemap helps search engines crawl and index your site more effectively."
];

// Constants
const MIN_TITLE = 30;
const MAX_TITLE = 60;
const MIN_DESCRIPTION = 120;
const MAX_DESCRIPTION = 160;

// Import shared components from KeywordAnalysis
// Status Icon Component
const StatusIcon = ({ status }) => {
  switch (status) {
    case 'good':
      return (
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="10" cy="10" r="9" opacity="0.25"></circle>
          <path d="M6 10l2.5 2.5L14 7"></path>
        </svg>
      );
    case 'warning':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
      );
    case 'error':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      );
    default:
      return null;
  }
};

// Circular Progress Component
const CircularProgress = ({ percentage, size = 80, strokeWidth = 8 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;
  
  const getColor = () => {
    if (percentage >= 80) return 'var(--success-color)';
    if (percentage >= 50) return 'var(--warning-color)';
    return 'var(--error-color)';
  };

  return (
    <div className="circular-progress-container" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="circular-progress-svg">
        <circle
          className="circular-progress-bg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <circle
          className="circular-progress-fg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          stroke={getColor()}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="circular-progress-text">
        <span className="circular-progress-percentage">{Math.round(percentage)}</span>
      </div>
    </div>
  );
};

// Detail Row Component
// eslint-disable-next-line no-unused-vars
const DetailRow = ({ label, value, remark, status = 'good', additionalContent }) => {
  const resolvedValue =
    value !== undefined && value !== null && value !== ''
      ? value
      : <span className="muted">Not available</span>;

  return (
    <div className={`detail-row detail-${status}`}>
      <div className="row-status-section">
        <div className={`row-status-icon status-${status}`}>
          <StatusIcon status={status} />
        </div>
      </div>
      <div className="row-metric-section">
        <div className="row-label">{label}</div>
      </div>
      <div className="row-remark-section">
        <div className="combined-explanation">
          <span className="explanation-value">{resolvedValue}</span>
          {remark && (
            <span className="explanation-remark"> — {remark}</span>
          )}
        </div>
        {additionalContent && (
          <div className="additional-content">
            {additionalContent}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper functions
const formatHeadings = (items = []) => {
  if (!items.length) return <span className="muted">No data</span>;
  return (
    <div className="heading-container">
      {items.map((item, idx) => (
        <div key={idx} className="heading-box">
          {item}
        </div>
      ))}
    </div>
  );
};

const formatTags = (tags = []) => {
  if (!tags.length) return <span className="muted">No data</span>;
  return (
    <div className="tag-container">
      {tags.map((tag, idx) => (
        <span key={idx} className="schema-tag">
          {tag.replace('https://schema.org/', '').replace('http://schema.org/', '')}
        </span>
      ))}
    </div>
  );
};

const getImageTitle = (img, src) => {
  if (typeof img === 'object' && img.title && img.title.trim()) {
    return img.title;
  }
  try {
    const url = new URL(src);
    const pathname = url.pathname;
    const filename = pathname.split('/').pop();
    if (filename && filename.includes('.')) {
      const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
      if (nameWithoutExt) {
        return decodeURIComponent(nameWithoutExt)
          .replace(/[-_]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-useless-escape
    const match = src.match(/\/([^\/]+\.(jpg|jpeg|png|gif|webp|svg))$/i);
    if (match && match[1]) {
      return match[1].split('.')[0].replace(/[-_]/g, ' ').trim();
    }
  }
  return null;
};

// eslint-disable-next-line no-unused-vars
const formatImageList = (images, options = {}) => {
  const { showSize = false, remarkText = '', hideRemarks = false } = options;
  if (!images.length) return <span className="muted">No data</span>;
  
  return (
    <div className="image-list-container">
      <div className="image-preview-item image-preview-header">
        <div className="image-preview-section image-preview-left">
          <span className="image-header-label">Image</span>
        </div>
        <div className="image-preview-section image-preview-info">
          <span className="image-header-label">Details</span>
        </div>
        {!hideRemarks && (
          <div className="image-preview-section image-preview-remark">
            <span className="image-header-label">Remarks</span>
          </div>
        )}
      </div>
      {images.map((img, idx) => {
        const src = typeof img === 'string' ? img : img.src;
        let title = typeof img === 'object' && img.title ? img.title : null;
        if (!title || !title.trim()) {
          title = getImageTitle(img, src);
        }
        const size = typeof img === 'object' && img.size ? img.size : null;
        const resolvedRemark = typeof remarkText === 'function' ? remarkText(img) : remarkText || '';

        return (
          <div key={idx} className="image-preview-item image-preview-row">
            <div className="image-preview-section image-preview-left">
              <img 
                src={src} 
                alt="Preview" 
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
                className="image-thumbnail"
              />
              <div className="image-placeholder" style={{ display: 'none' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
              </div>
            </div>
            <div className="image-preview-section image-preview-info">
              {title && title.trim() && (
                <div className="image-title">{title}</div>
              )}
              <a href={src} target="_blank" rel="noopener noreferrer" className="image-link">
                {src}
              </a>
              {showSize && size && (
                <span className="image-size">{size}</span>
              )}
            </div>
            {!hideRemarks && (
              <div className="image-preview-section image-preview-remark">
                <div className="image-remark-label">Recommended fix</div>
                <p className="image-remark-text">
                  {resolvedRemark || 'Review this asset for potential improvements.'}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// eslint-disable-next-line no-unused-vars
const formatImageCards = (images, options = {}) => {
  const { showSize = false, remarkText = '' } = options;
  if (!images.length) return <span className="muted">No data</span>;
  
  return (
    <div className="image-cards-container">
      {images.map((img, idx) => {
        const src = typeof img === 'string' ? img : img.src;
        let title = typeof img === 'object' && img.title ? img.title : null;
        if (!title || !title.trim()) {
          title = getImageTitle(img, src);
        }
        const size = typeof img === 'object' && img.size ? img.size : null;
        const resolvedRemark = typeof remarkText === 'function' ? remarkText(img) : remarkText || '';

        return (
          <div key={idx} className="image-card">
            <div className="image-card-image">
              <img 
                src={src} 
                alt={title || 'Preview'} 
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
                className="image-card-thumbnail"
              />
              <div className="image-card-placeholder" style={{ display: 'none' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
              </div>
            </div>
            <div className="image-card-details">
              {title && title.trim() && (
                <div className="image-card-title">{title}</div>
              )}
              <a href={src} target="_blank" rel="noopener noreferrer" className="image-card-link">
                {src.length > 80 ? `${src.substring(0, 80)}...` : src}
              </a>
              {showSize && size && (
                <div className="image-card-size">{size}</div>
              )}
              {resolvedRemark && (
                <div className="image-card-remark">
                  <div className="image-card-remark-label">Recommended fix</div>
                  <p className="image-card-remark-text">{resolvedRemark}</p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const formatImageThumbnails = (images, options = {}) => {
  const { remarkText = '', showSize = false } = options;
  if (!images.length) return <span className="muted">No data</span>;
  
  return (
    <div className="image-thumbnails-container">
      {images.map((img, idx) => {
        const src = typeof img === 'string' ? img : img.src;
        let title = typeof img === 'object' && img.title ? img.title : null;
        if (!title || !title.trim()) {
          title = getImageTitle(img, src);
        }
        const size = typeof img === 'object' && img.size ? img.size : null;
        const resolvedRemark = typeof remarkText === 'function' ? remarkText(img) : remarkText || '';

        return (
          <div key={idx} className="image-thumbnail-card">
            <div className="image-thumbnail-image">
              <img 
                src={src} 
                alt={title || 'Preview'} 
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'flex';
                  }
                }}
                className="image-thumbnail-icon"
              />
              <div className="image-thumbnail-placeholder" style={{ display: 'none' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
              </div>
            </div>
            <div className="image-thumbnail-details">
              {title && title.trim() && (
                <div className="image-thumbnail-title">{title}</div>
              )}
              <a href={src} target="_blank" rel="noopener noreferrer" className="image-thumbnail-link">
                {src.length > 60 ? `${src.substring(0, 60)}...` : src}
              </a>
              {showSize && size && (
                <div className="image-thumbnail-size">{size}</div>
              )}
              {resolvedRemark && (
                <div className="image-thumbnail-remark">
                  <p className="image-thumbnail-remark-text">{resolvedRemark}</p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// eslint-disable-next-line no-unused-vars
const formatLinkList = (links, options = {}) => {
  const { hideLinkType = false } = options;
  if (!links.length) return <span className="muted">No data</span>;

  return (
    <div className="link-list-container">
      <div className="link-preview-item link-preview-header">
        {!hideLinkType && (
          <div className="link-preview-section link-preview-type">
            <span className="link-header-label">Link Type</span>
          </div>
        )}
        <div className="link-preview-section link-preview-anchor">
          <span className="link-header-label">Anchor Text</span>
        </div>
        <div className="link-preview-section link-preview-url">
          <span className="link-header-label">URL</span>
        </div>
      </div>
      {links.map((link, idx) => {
        const tagLabel = link.tagLabelOverride
          ? link.tagLabelOverride
          : link.isNofollow
            ? 'Nofollow'
            : link.isExternal
              ? 'External'
              : 'Internal';
        const tagClass = link.tagClassOverride
          ? link.tagClassOverride
          : link.isNofollow
            ? 'tag-nofollow'
            : link.isExternal
              ? 'tag-external'
              : 'tag-internal';

        return (
          <div key={idx} className="link-preview-item link-preview-row">
            {!hideLinkType && (
              <div className="link-preview-section link-preview-type">
                <span className={`link-type-tag ${tagClass}`}>
                  {tagLabel}
                </span>
              </div>
            )}
            <div className="link-preview-section link-preview-anchor">
              {link.image ? (
                <div className="link-image-anchor">
                  <img src={link.image} alt="Link preview" onError={(e) => (e.target.style.display = 'none')} />
                  <span>Image Link</span>
                </div>
              ) : (
                <span className="link-anchor">{link.text && link.text.trim() ? link.text : '-'}</span>
              )}
            </div>
            <div className="link-preview-section link-preview-url">
              <a href={link.url} target="_blank" rel="noopener noreferrer" className="link-url">
                {link.url}
              </a>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const formatLinkCards = (links, options = {}) => {
  const { remarkText = '' } = options;
  if (!links.length) return <span className="muted">No data</span>;

  return (
    <div className="link-cards-container">
      {links.map((link, idx) => {
        const tagLabel = link.tagLabelOverride
          ? link.tagLabelOverride
          : link.isNofollow
            ? 'Nofollow'
            : link.isExternal
              ? 'External Link'
              : 'Internal Link';
        const tagClass = link.tagClassOverride
          ? link.tagClassOverride
          : link.isNofollow
            ? 'tag-nofollow'
            : link.isExternal
              ? 'tag-external'
              : 'tag-internal';
        
        const anchorText = link.text && link.text.trim() ? link.text : 'No anchor text';
        const resolvedRemark = typeof remarkText === 'function' ? remarkText(link) : remarkText || '';

        return (
          <div key={idx} className={`link-card ${tagClass === 'tag-broken' ? 'link-card-broken' : ''}`}>
            <div className="link-card-header">
              <span className={`link-card-type-tag ${tagClass}`}>
                {tagLabel}
              </span>
            </div>
            <div className="link-card-content">
              {link.image ? (
                <div className="link-card-image">
                  <div className="link-card-image-label">Image Link</div>
                  <img 
                    src={link.image} 
                    alt="Link preview" 
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) {
                        e.target.nextSibling.style.display = 'flex';
                      }
                    }}
                    className="link-card-thumbnail"
                  />
                  <div className="link-card-image-placeholder" style={{ display: 'none' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                  </div>
                </div>
              ) : (
                <div className="link-card-anchor">
                  <div className="link-card-anchor-label">Anchor Text</div>
                  <div className="link-card-anchor-text">{anchorText}</div>
                </div>
              )}
              <div className="link-card-url-section">
                <div className="link-card-url-label">URL</div>
                <a href={link.url} target="_blank" rel="noopener noreferrer" className="link-card-url">
                  {link.url.length > 80 ? `${link.url.substring(0, 80)}...` : link.url}
                </a>
              </div>
              {resolvedRemark && (
                <div className="link-card-remark">
                  <div className="link-card-remark-label">Note</div>
                  <p className="link-card-remark-text">{resolvedRemark}</p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const getLengthFeedback = (text, min, max, label) => {
  const labelLower = label.toLowerCase();
  if (!text) {
    return {
      remark: `You don't have a ${labelLower} set. This is the text that appears in search results and browser tabs. Add one that describes your page clearly and includes important keywords.`,
      status: 'warning',
    };
  }
  if (text.length < min) {
    return {
      remark: `Your ${labelLower} is too short (${text.length} characters). Search engines prefer ${min}–${max} characters. Make it longer to better describe your page and improve how it appears in search results.`,
      status: 'warning',
    };
  }
  if (text.length > max) {
    return {
      remark: `Your ${labelLower} is too long (${text.length} characters). Search engines typically cut off text after ${max} characters, so keep it between ${min}–${max} characters to ensure the full message is visible.`,
      status: 'warning',
    };
  }
  return {
    remark: `Your ${labelLower} is the perfect length (${text.length} characters). This helps it display fully in search results and gives you the best chance of attracting clicks.`,
    status: 'good',
  };
};

const getRobotsRemark = (robots) => {
  if (!robots.exists) {
    return {
      remark: 'Your website doesn\'t have a robots.txt file. This file tells search engines which pages they can and cannot visit. You should create one to have better control over how your site is crawled.',
      status: 'warning',
    };
  }
  if (!robots.accessible) {
    return {
      remark: 'Your robots.txt file exists but search engines can\'t access it. This might be due to server settings or permissions. Fix this so search engines can read your crawling instructions.',
      status: 'warning',
    };
  }
  return {
    remark: 'Your robots.txt file is working properly and search engines can access it. Make sure the rules inside match what you want search engines to see.',
    status: 'good',
  };
};

const getSchemaRemark = (schema) => {
  if (!schema.found) {
    return {
      remark: 'Your website doesn\'t use structured data (schema markup). This special code helps search engines understand your content better and can make your listings look more attractive in search results with stars, prices, or other rich information.',
      status: 'warning',
    };
  }
  return {
    remark: `Great! Your website uses structured data (${schema.types.length || 1} type${schema.types.length !== 1 ? 's' : ''}). This helps search engines understand your content and can make your search listings more appealing. Make sure to test it with Google's Rich Results Test tool.`,
    status: 'good',
  };
};

const getSitemapRemark = (sitemaps) => {
  if (!sitemaps.found) {
    return {
      remark: 'No sitemap found. A sitemap is like a map of your website that helps search engines discover all your pages. Create a sitemap.xml file and tell search engines about it through Google Search Console.',
      status: 'warning',
    };
  }
  return {
    remark: `Good! You have ${sitemaps.sitemapUrls.length} sitemap${sitemaps.sitemapUrls.length !== 1 ? 's' : ''} that help search engines find your pages. Keep them updated whenever you add or remove pages from your website.`,
    status: 'good',
  };
};

const getContentRemark = (content) => {
  if (content.wordCount < 300) {
    return {
      remark: `Your page has ${content.wordCount} words, which is quite short. Search engines and visitors prefer pages with more substantial content (typically 300+ words). Add more detailed information, examples, or explanations to make your page more valuable.`,
      status: 'warning',
    };
  }
  return {
    remark: `Your page has ${content.wordCount} words, which is a good amount of content. This gives you enough space to provide valuable information to visitors and helps search engines understand your page better. Keep your content well-organized and update it regularly.`,
    status: 'good',
  };
};

const getImageRemark = (images) => {
  if (images.withoutAlt > 0) {
    return {
      remark: `${images.withoutAlt} of your images don't have alt text (alternative text). Alt text describes images to people who can't see them and helps search engines understand what your images show. Add descriptive alt text to all images for better accessibility and SEO.`,
      status: 'warning',
    };
  }
  return {
    remark: 'Excellent! All your images have alt text. This helps visually impaired visitors understand your images and helps search engines index your images properly. Keep your alt text descriptions clear and relevant to each image.',
    status: 'good',
  };
};

const getLinkRemark = (links) => {
  if (links.external > links.internal) {
    return {
      remark: 'You have more links pointing to other websites than links to other pages on your own site. While external links are fine, having more internal links helps visitors navigate your site and helps search engines discover all your pages. Try adding more links to your other relevant pages.',
      status: 'warning',
    };
  }
  return {
    remark: 'You have a good balance of internal links (to your own pages) and external links (to other sites). Internal links help visitors explore your site and help search engines find all your content. Keep this balance while making sure your links are relevant and useful.',
    status: 'good',
  };
};

const getKeywordRemark = (keywords) => {
  if (!keywords.topKeywords.length) {
    return 'We couldn\'t find any keywords that appear multiple times on your page. While you don\'t want to overuse keywords, having important terms appear naturally throughout your content helps search engines understand what your page is about.';
  }
  return 'We found the main keywords that appear most often on your page. Make sure these keywords match what people are searching for and that they appear naturally in your content—don\'t stuff them in just to rank higher.';
};

// Score calculation functions
const calculateSecurityScore = (security) => {
  let score = 0;
  let total = 3;
  
  if (security.sslCertificate) score += 1;
  if (security.httpsRedirection) score += 1;
  if (security.wwwRedirection?.redirected) score += 1;
  
  return (score / total) * 100;
};

const calculateRobotsScore = (robots) => {
  if (!robots.exists) return 0;
  if (!robots.accessible) return 50;
  return 100;
};

const calculateSchemaSitemapScore = (schema, sitemaps) => {
  let score = 0;
  let total = 2;
  
  if (schema.found) score += 1;
  if (sitemaps.found) score += 1;
  
  return (score / total) * 100;
};

const calculateMetaScore = (meta) => {
  let score = 0;
  let total = 4;
  
  if (meta.title && meta.title.length >= MIN_TITLE && meta.title.length <= MAX_TITLE) score += 1;
  if (meta.description && meta.description.length >= MIN_DESCRIPTION && meta.description.length <= MAX_DESCRIPTION) score += 1;
  if (meta.indexingStatus === 'index') score += 1;
  if (meta.ogImage) score += 1;
  
  return (score / total) * 100;
};

const calculateTechnicalScore = (meta) => {
  let score = 0;
  let total = 3;
  
  if (meta.viewport) score += 1;
  if (meta.charset) score += 1;
  if (meta.language) score += 1;
  
  return (score / total) * 100;
};

const calculateHeadingScore = (headings) => {
  let score = 0;
  let total = 2;
  
  if (headings.h1Count === 1) score += 1;
  if (headings.h2.length > 0) score += 1;
  
  return (score / total) * 100;
};

const calculateKeywordScore = (keywords) => {
  if (keywords.topKeywords.length > 0) return 100;
  return 50;
};

const calculateContentScore = (content) => {
  if (content.wordCount >= 300) return 100;
  if (content.wordCount >= 150) return 70;
  return 40;
};

const calculateImageScore = (images) => {
  if (images.total === 0) return 100;
  
  let score = 0;
  let total = 3;
  
  if (images.withoutAlt === 0) score += 1;
  else if (images.withAlt > 0) score += (images.withAlt / images.total);
  
  if (images.largeImages === 0) score += 1;
  
  if (images.missingWebpList.length === 0) score += 1;
  else if (images.total > 0) {
    const webpCoverage = (images.total - images.missingWebpList.length) / images.total;
    score += webpCoverage;
  }
  
  return (score / total) * 100;
};

const calculateLinkScore = (links) => {
  if (links.total === 0) return 50;
  if (links.internal >= links.external) return 100;
  return 70;
};

// Comparison Row Component for 3-column layout
const ComparisonRow = ({ label, ourValue, competitorValue, ourStatus, competitorStatus, ourRemark, competitorRemark, ourAdditionalContent, competitorAdditionalContent }) => {
  const resolveValue = (value) => {
    if (value === undefined || value === null || value === '') {
      return <span className="muted">Not available</span>;
    }
    return typeof value === 'function' ? value() : value;
  };

  return (
    <div className="comparison-row">
      <div className="comparison-metric-column">
        <div className="comparison-metric-label">{label}</div>
      </div>
      <div className={`comparison-value-column ${ourStatus ? `comparison-${ourStatus}` : ''}`}>
        <div className="comparison-value-content">
          {ourStatus && (
            <div className="comparison-status-icon">
              <StatusIcon status={ourStatus} />
            </div>
          )}
          <div className="comparison-value-text">
            <div className="comparison-value">{resolveValue(ourValue)}</div>
            {ourRemark && (
              <div className="comparison-remark">{ourRemark}</div>
            )}
            {ourAdditionalContent && (
              <div className="comparison-additional-content">
                {ourAdditionalContent}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={`comparison-value-column ${competitorStatus ? `comparison-${competitorStatus}` : ''}`}>
        <div className="comparison-value-content">
          {competitorStatus && (
            <div className="comparison-status-icon">
              <StatusIcon status={competitorStatus} />
            </div>
          )}
          <div className="comparison-value-text">
            <div className="comparison-value">{resolveValue(competitorValue)}</div>
            {competitorRemark && (
              <div className="comparison-remark">{competitorRemark}</div>
            )}
            {competitorAdditionalContent && (
              <div className="comparison-additional-content">
                {competitorAdditionalContent}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Comparison Sections Component for 3-column layout
const ComparisonSections = ({ ourResults, competitorResults }) => {
  if (!ourResults?.auditResults || !competitorResults?.auditResults) return null;

  const our = ourResults.auditResults;
  const competitor = competitorResults.auditResults;

  return (
    <>
      {/* Security & Redirection Analysis */}
      <section className="result-section">
        <div className="section-header comparison-row">
          <div className="comparison-metric-column">
            <h3 className="section-title">Security & Redirection Analysis</h3>
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateSecurityScore(our.security)} />
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateSecurityScore(competitor.security)} />
          </div>
        </div>
        <div className="section-content">
          <ComparisonRow
            label="SSL Certificate"
            ourValue={our.security.sslCertificate ? 'Valid certificate detected' : 'Not detected'}
            competitorValue={competitor.security.sslCertificate ? 'Valid certificate detected' : 'Not detected'}
            ourStatus={our.security.sslCertificate ? 'good' : 'warning'}
            competitorStatus={competitor.security.sslCertificate ? 'good' : 'warning'}
            ourRemark={our.security.sslCertificate
              ? 'Your website uses HTTPS (the secure version of HTTP), which encrypts data between visitors and your site. This protects sensitive information and makes visitors feel safe. Make sure to renew your certificate before it expires to avoid browser security warnings.'
              : 'Your website doesn\'t have an SSL certificate, which means it\'s not using HTTPS. This is important for security and trust—visitors expect to see the padlock icon in their browser. Install an SSL certificate (many hosting providers offer free ones) to encrypt your website traffic.'}
            competitorRemark={competitor.security.sslCertificate
              ? 'Your website uses HTTPS (the secure version of HTTP), which encrypts data between visitors and your site. This protects sensitive information and makes visitors feel safe. Make sure to renew your certificate before it expires to avoid browser security warnings.'
              : 'Your website doesn\'t have an SSL certificate, which means it\'s not using HTTPS. This is important for security and trust—visitors expect to see the padlock icon in their browser. Install an SSL certificate (many hosting providers offer free ones) to encrypt your website traffic.'}
          />
          <ComparisonRow
            label="HTTPS Handling"
            ourValue={our.security.httpsRedirection ? 'HTTPS enforced' : 'HTTP allowed'}
            competitorValue={competitor.security.httpsRedirection ? 'HTTPS enforced' : 'HTTP allowed'}
            ourStatus={our.security.httpsRedirection ? 'good' : 'warning'}
            competitorStatus={competitor.security.httpsRedirection ? 'good' : 'warning'}
            ourRemark={our.security.httpsRedirection
              ? 'Great! Your website automatically redirects all visitors from the insecure HTTP version to the secure HTTPS version. This ensures everyone uses the encrypted connection, which is better for security and SEO.'
              : 'Your website allows both HTTP and HTTPS access. You should set up automatic redirects so all visitors are sent to the HTTPS version. This prevents duplicate content issues and ensures everyone uses the secure connection.'}
            competitorRemark={competitor.security.httpsRedirection
              ? 'Great! Your website automatically redirects all visitors from the insecure HTTP version to the secure HTTPS version. This ensures everyone uses the encrypted connection, which is better for security and SEO.'
              : 'Your website allows both HTTP and HTTPS access. You should set up automatic redirects so all visitors are sent to the HTTPS version. This prevents duplicate content issues and ensures everyone uses the secure connection.'}
          />
          <ComparisonRow
            label="WWW Redirection"
            ourValue={our.security?.wwwRedirection?.type || 'No redirection set'}
            competitorValue={competitor.security?.wwwRedirection?.type || 'No redirection set'}
            ourStatus={our.security?.wwwRedirection?.redirected ? 'good' : 'error'}
            competitorStatus={competitor.security?.wwwRedirection?.redirected ? 'good' : 'error'}
            ourRemark={our.security?.wwwRedirection?.redirected
              ? 'Your website properly redirects between www and non-www versions (like example.com and www.example.com). This is good—just make sure all your internal links use the same version consistently.'
              : 'This is a critical SEO issue! Your website doesn\'t redirect between www and non-www versions. Search engines may see your site as two separate websites, which can cause duplicate content problems and split your SEO rankings. You must choose one version (either with www or without www) and set up a permanent redirect from the other version immediately.'}
            competitorRemark={competitor.security?.wwwRedirection?.redirected
              ? 'This competitor\'s website properly redirects between www and non-www versions (like example.com and www.example.com). This is good for SEO—it prevents duplicate content issues.'
              : 'This competitor\'s website doesn\'t redirect between www and non-www versions. Search engines may see it as two separate websites, which can cause duplicate content problems and split SEO rankings.'}
          />
        </div>
      </section>

      {/* Robots & Crawling Analysis */}
      <section className="result-section">
        <div className="section-header comparison-row">
          <div className="comparison-metric-column">
            <h3 className="section-title">Robots & Crawling Analysis</h3>
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateRobotsScore(our.robots)} />
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateRobotsScore(competitor.robots)} />
          </div>
        </div>
        <div className="section-content">
          {(() => {
            const ourRobotsRemark = getRobotsRemark(our.robots);
            const competitorRobotsRemark = getRobotsRemark(competitor.robots);
            return (
              <>
                <ComparisonRow
                  label="robots.txt"
                  ourValue={our.robots.exists ? 'Found' : 'Not found'}
                  competitorValue={competitor.robots.exists ? 'Found' : 'Not found'}
                  ourStatus={ourRobotsRemark.status}
                  competitorStatus={competitorRobotsRemark.status}
                  ourRemark={ourRobotsRemark.remark}
                  competitorRemark={competitorRobotsRemark.remark}
                  ourAdditionalContent={
                    our.robots.exists && our.robots.content ? (
                      <div>
                        <div className="additional-content-label">robots.txt Content:</div>
                        <pre className="code-block">{our.robots.content.substring(0, 500)}...</pre>
                      </div>
                    ) : null
                  }
                  competitorAdditionalContent={
                    competitor.robots.exists && competitor.robots.content ? (
                      <div>
                        <div className="additional-content-label">robots.txt Content:</div>
                        <pre className="code-block">{competitor.robots.content.substring(0, 500)}...</pre>
                      </div>
                    ) : null
                  }
                />
                {(our.robots.exists || competitor.robots.exists) && (
                  <ComparisonRow
                    label="Accessibility"
                    ourValue={our.robots.exists ? (our.robots.accessible ? 'Reachable (HTTP 200)' : 'Blocked or 4xx') : 'N/A'}
                    competitorValue={competitor.robots.exists ? (competitor.robots.accessible ? 'Reachable (HTTP 200)' : 'Blocked or 4xx') : 'N/A'}
                    ourStatus={our.robots.exists ? (our.robots.accessible ? 'good' : 'warning') : null}
                    competitorStatus={competitor.robots.exists ? (competitor.robots.accessible ? 'good' : 'warning') : null}
                    ourRemark={our.robots.exists
                      ? (our.robots.accessible
                        ? 'Search engines can successfully read your robots.txt file. This is good—just make sure the rules inside are correct and up to date with your current website structure.'
                        : 'Search engines can\'t access your robots.txt file, which means they can\'t read your crawling instructions. Check your server settings to make sure the file is publicly accessible and returns a 200 status code.')
                      : null}
                    competitorRemark={competitor.robots.exists
                      ? (competitor.robots.accessible
                        ? 'Search engines can successfully read your robots.txt file. This is good—just make sure the rules inside are correct and up to date with your current website structure.'
                        : 'Search engines can\'t access your robots.txt file, which means they can\'t read your crawling instructions. Check your server settings to make sure the file is publicly accessible and returns a 200 status code.')
                      : null}
                  />
                )}
              </>
            );
          })()}
        </div>
      </section>

      {/* Structured Data & Sitemaps Analysis */}
      <section className="result-section">
        <div className="section-header comparison-row">
          <div className="comparison-metric-column">
            <h3 className="section-title">Structured Data & Sitemaps Analysis</h3>
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateSchemaSitemapScore(our.schema, our.sitemaps)} />
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateSchemaSitemapScore(competitor.schema, competitor.sitemaps)} />
          </div>
        </div>
        <div className="section-content">
          {(() => {
            const ourSchemaSummary = getSchemaRemark(our.schema);
            const competitorSchemaSummary = getSchemaRemark(competitor.schema);
            const ourSitemapSummary = getSitemapRemark(our.sitemaps);
            const competitorSitemapSummary = getSitemapRemark(competitor.sitemaps);
            return (
              <>
                <ComparisonRow
                  label="Schema Markup"
                  ourValue={our.schema.found ? 'Detected' : 'Not detected'}
                  competitorValue={competitor.schema.found ? 'Detected' : 'Not detected'}
                  ourStatus={ourSchemaSummary.status}
                  competitorStatus={competitorSchemaSummary.status}
                  ourRemark={ourSchemaSummary.remark}
                  competitorRemark={competitorSchemaSummary.remark}
                  ourAdditionalContent={
                    our.schema.found && our.schema.types.length > 0 ? (
                      <div>
                        <div className="additional-content-label">Detected Schema Types:</div>
                        {formatTags(our.schema.types)}
                      </div>
                    ) : null
                  }
                  competitorAdditionalContent={
                    competitor.schema.found && competitor.schema.types.length > 0 ? (
                      <div>
                        <div className="additional-content-label">Detected Schema Types:</div>
                        {formatTags(competitor.schema.types)}
                      </div>
                    ) : null
                  }
                />
                <ComparisonRow
                  label="Sitemaps"
                  ourValue={our.sitemaps.found ? 'Declared' : 'Not declared'}
                  competitorValue={competitor.sitemaps.found ? 'Declared' : 'Not declared'}
                  ourStatus={ourSitemapSummary.status}
                  competitorStatus={competitorSitemapSummary.status}
                  ourRemark={ourSitemapSummary.remark}
                  competitorRemark={competitorSitemapSummary.remark}
                  ourAdditionalContent={
                    our.sitemaps.sitemapUrls.length > 0 ? (
                      <div>
                        <div className="additional-content-label">Sitemap URLs:</div>
                        <div className="row-link-stack">
                          {our.sitemaps.sitemapUrls.map((sitemapUrl, idx) => (
                            <a key={idx} href={sitemapUrl} target="_blank" rel="noopener noreferrer">
                              {sitemapUrl}
                            </a>
                          ))}
                        </div>
                      </div>
                    ) : null
                  }
                  competitorAdditionalContent={
                    competitor.sitemaps.sitemapUrls.length > 0 ? (
                      <div>
                        <div className="additional-content-label">Sitemap URLs:</div>
                        <div className="row-link-stack">
                          {competitor.sitemaps.sitemapUrls.map((sitemapUrl, idx) => (
                            <a key={idx} href={sitemapUrl} target="_blank" rel="noopener noreferrer">
                              {sitemapUrl}
                            </a>
                          ))}
                        </div>
                      </div>
                    ) : null
                  }
                />
              </>
            );
          })()}
        </div>
      </section>

      {/* Meta Information Analysis */}
      <section className="result-section">
        <div className="section-header comparison-row">
          <div className="comparison-metric-column">
            <h3 className="section-title">Meta Information Analysis</h3>
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateMetaScore(our.meta)} />
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateMetaScore(competitor.meta)} />
          </div>
        </div>
        <div className="section-content">
          {(() => {
            const ourTitleFeedback = getLengthFeedback(our.meta.title, MIN_TITLE, MAX_TITLE, 'Meta title');
            const competitorTitleFeedback = getLengthFeedback(competitor.meta.title, MIN_TITLE, MAX_TITLE, 'Meta title');
            const ourDescFeedback = getLengthFeedback(our.meta.description, MIN_DESCRIPTION, MAX_DESCRIPTION, 'Meta description');
            const competitorDescFeedback = getLengthFeedback(competitor.meta.description, MIN_DESCRIPTION, MAX_DESCRIPTION, 'Meta description');
            return (
              <>
                <ComparisonRow
                  label="Meta Title"
                  ourValue={our.meta.title}
                  competitorValue={competitor.meta.title}
                  ourStatus={ourTitleFeedback.status}
                  competitorStatus={competitorTitleFeedback.status}
                  ourRemark={ourTitleFeedback.remark}
                  competitorRemark={competitorTitleFeedback.remark}
                />
                <ComparisonRow
                  label="Meta Description"
                  ourValue={our.meta.description}
                  competitorValue={competitor.meta.description}
                  ourStatus={ourDescFeedback.status}
                  competitorStatus={competitorDescFeedback.status}
                  ourRemark={ourDescFeedback.remark}
                  competitorRemark={competitorDescFeedback.remark}
                />
              </>
            );
          })()}
          <ComparisonRow
            label="Meta Robots"
            ourValue={our.meta.robots || 'Not specified'}
            competitorValue={competitor.meta.robots || 'Not specified'}
            ourStatus={our.meta.robots?.toLowerCase().includes('noindex') ? 'error' : 'good'}
            competitorStatus={competitor.meta.robots?.toLowerCase().includes('noindex') ? 'error' : 'good'}
            ourRemark={
              our.meta.robots?.toLowerCase().includes('noindex')
                ? 'Your page is set to "noindex", which tells search engines not to show this page in search results. Only use this if you intentionally want to hide the page from search. If you want this page to appear in search results, remove the noindex directive.'
                : 'Your page allows search engines to index it, which means it can appear in search results. This is usually what you want unless you have a specific reason to hide the page.'}
            competitorRemark={
              competitor.meta.robots?.toLowerCase().includes('noindex')
                ? 'Your page is set to "noindex", which tells search engines not to show this page in search results. Only use this if you intentionally want to hide the page from search. If you want this page to appear in search results, remove the noindex directive.'
                : 'Your page allows search engines to index it, which means it can appear in search results. This is usually what you want unless you have a specific reason to hide the page.'}
          />
          <ComparisonRow
            label="Indexing Status"
            ourValue={our.meta.indexingStatus}
            competitorValue={competitor.meta.indexingStatus}
            ourStatus={our.meta.indexingStatus === 'index' ? 'good' : 'warning'}
            competitorStatus={competitor.meta.indexingStatus === 'index' ? 'good' : 'warning'}
            ourRemark={
              our.meta.indexingStatus === 'index'
                ? 'Your page is set to be indexed by search engines, which means it can appear in search results. Monitor your page\'s performance in Google Search Console to see how it\'s performing.'
                : 'Your page is set to "noindex", which prevents it from appearing in search results. If you want people to find this page through search, you need to remove the noindex setting.'}
            competitorRemark={
              competitor.meta.indexingStatus === 'index'
                ? 'Your page is set to be indexed by search engines, which means it can appear in search results. Monitor your page\'s performance in Google Search Console to see how it\'s performing.'
                : 'Your page is set to "noindex", which prevents it from appearing in search results. If you want people to find this page through search, you need to remove the noindex setting.'}
          />
          <ComparisonRow
            label="Open Graph Image"
            ourValue={
              our.meta.ogImage
                ? (
                  <div className="og-image-inline">
                    <img src={our.meta.ogImage} alt="OG preview" onError={(e) => (e.target.style.display = 'none')} />
                    <a href={our.meta.ogImage} target="_blank" rel="noopener noreferrer">
                      {our.meta.ogImage}
                    </a>
                    {our.meta.ogImageDimensions && (
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                        Dimensions: {our.meta.ogImageDimensions.width} × {our.meta.ogImageDimensions.height} pixels
                      </div>
                    )}
                  </div>
                )
                : null
            }
            competitorValue={
              competitor.meta.ogImage
                ? (
                  <div className="og-image-inline">
                    <img src={competitor.meta.ogImage} alt="OG preview" onError={(e) => (e.target.style.display = 'none')} />
                    <a href={competitor.meta.ogImage} target="_blank" rel="noopener noreferrer">
                      {competitor.meta.ogImage}
                    </a>
                    {competitor.meta.ogImageDimensions && (
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                        Dimensions: {competitor.meta.ogImageDimensions.width} × {competitor.meta.ogImageDimensions.height} pixels
                      </div>
                    )}
                  </div>
                )
                : null
            }
            ourStatus={
              our.meta.ogImage
                ? (() => {
                    const isOptimalSize = our.meta.ogImageDimensions && 
                      our.meta.ogImageDimensions.width === 1200 && 
                      our.meta.ogImageDimensions.height === 630;
                    return isOptimalSize ? 'good' : 'warning';
                  })()
                : 'warning'
            }
            competitorStatus={
              competitor.meta.ogImage
                ? (() => {
                    const isOptimalSize = competitor.meta.ogImageDimensions && 
                      competitor.meta.ogImageDimensions.width === 1200 && 
                      competitor.meta.ogImageDimensions.height === 630;
                    return isOptimalSize ? 'good' : 'warning';
                  })()
                : 'warning'
            }
            ourRemark={
              our.meta.ogImage
                ? (() => {
                    const isOptimalSize = our.meta.ogImageDimensions && 
                      our.meta.ogImageDimensions.width === 1200 && 
                      our.meta.ogImageDimensions.height === 630;
                    if (isOptimalSize) {
                      return 'You have an Open Graph image set up with the optimal dimensions (1200×630 pixels), which is the image that appears when someone shares your page on social media. This size ensures your image looks great on all social platforms.';
                    } else if (our.meta.ogImageDimensions) {
                      return `You have an Open Graph image set up, which is the image that appears when someone shares your page on social media. However, your image is ${our.meta.ogImageDimensions.width}×${our.meta.ogImageDimensions.height} pixels. For the best appearance on social media, use an image that's exactly 1200×630 pixels.`;
                    } else {
                      return 'You have an Open Graph image set up, which is the image that appears when someone shares your page on social media. For the best appearance, use an image that\'s 1200×630 pixels.';
                    }
                  })()
                : 'You don\'t have an Open Graph image set up. When someone shares your page on social media, they\'ll see a generic preview. Add an og:image tag to control what image appears in social media shares.'}
            competitorRemark={
              competitor.meta.ogImage
                ? (() => {
                    const isOptimalSize = competitor.meta.ogImageDimensions && 
                      competitor.meta.ogImageDimensions.width === 1200 && 
                      competitor.meta.ogImageDimensions.height === 630;
                    if (isOptimalSize) {
                      return 'You have an Open Graph image set up with the optimal dimensions (1200×630 pixels), which is the image that appears when someone shares your page on social media. This size ensures your image looks great on all social platforms.';
                    } else if (competitor.meta.ogImageDimensions) {
                      return `You have an Open Graph image set up, which is the image that appears when someone shares your page on social media. However, your image is ${competitor.meta.ogImageDimensions.width}×${competitor.meta.ogImageDimensions.height} pixels. For the best appearance on social media, use an image that's exactly 1200×630 pixels.`;
                    } else {
                      return 'You have an Open Graph image set up, which is the image that appears when someone shares your page on social media. For the best appearance, use an image that\'s 1200×630 pixels.';
                    }
                  })()
                : 'You don\'t have an Open Graph image set up. When someone shares your page on social media, they\'ll see a generic preview. Add an og:image tag to control what image appears in social media shares.'}
          />
        </div>
      </section>

      {/* Technical Analysis */}
      <section className="result-section">
        <div className="section-header comparison-row">
          <div className="comparison-metric-column">
            <h3 className="section-title">Technical Analysis</h3>
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateTechnicalScore(our.meta)} />
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateTechnicalScore(competitor.meta)} />
          </div>
        </div>
        <div className="section-content">
          <ComparisonRow
            label="Viewport"
            ourValue={our.meta.viewport || 'Not set'}
            competitorValue={competitor.meta.viewport || 'Not set'}
            ourStatus={our.meta.viewport ? 'good' : 'warning'}
            competitorStatus={competitor.meta.viewport ? 'good' : 'warning'}
            ourRemark={
              our.meta.viewport
                ? 'Your page has a viewport setting, which helps it display properly on mobile devices. This is essential for mobile-friendly websites. Keep testing your site on real phones and tablets to ensure it looks good.'
                : 'Your page doesn\'t have a viewport meta tag, which means it might not display correctly on mobile devices. Add a viewport tag so your website adapts properly to different screen sizes.'}
            competitorRemark={
              competitor.meta.viewport
                ? 'Your page has a viewport setting, which helps it display properly on mobile devices. This is essential for mobile-friendly websites. Keep testing your site on real phones and tablets to ensure it looks good.'
                : 'Your page doesn\'t have a viewport meta tag, which means it might not display correctly on mobile devices. Add a viewport tag so your website adapts properly to different screen sizes.'}
          />
          <ComparisonRow
            label="Charset"
            ourValue={our.meta.charset || 'Not specified'}
            competitorValue={competitor.meta.charset || 'Not specified'}
            ourStatus={our.meta.charset ? 'good' : 'error'}
            competitorStatus={competitor.meta.charset ? 'good' : 'error'}
            ourRemark={
              our.meta.charset
                ? 'Your page specifies its character encoding (UTF-8), which ensures special characters and symbols display correctly. This is important for international content and prevents text from appearing as garbled characters.'
                : 'Your page doesn\'t specify a character encoding. While modern browsers usually handle this automatically, it\'s best practice to declare UTF-8 in your page\'s head section to ensure all characters display correctly.'}
            competitorRemark={
              competitor.meta.charset
                ? 'Your page specifies its character encoding (UTF-8), which ensures special characters and symbols display correctly. This is important for international content and prevents text from appearing as garbled characters.'
                : 'Your page doesn\'t specify a character encoding. While modern browsers usually handle this automatically, it\'s best practice to declare UTF-8 in your page\'s head section to ensure all characters display correctly.'}
          />
          <ComparisonRow
            label="Language"
            ourValue={our.meta.language || 'Not specified'}
            competitorValue={competitor.meta.language || 'Not specified'}
            ourStatus={our.meta.language ? 'good' : 'error'}
            competitorStatus={competitor.meta.language ? 'good' : 'error'}
            ourRemark={
              our.meta.language
                ? 'Your page declares its language, which helps search engines understand who your content is for and can improve how your page appears in search results for that language.'
                : 'Your page doesn\'t specify its language. Adding a language attribute helps search engines understand your target audience and can improve your page\'s visibility in search results for that language.'}
            competitorRemark={
              competitor.meta.language
                ? 'Your page declares its language, which helps search engines understand who your content is for and can improve how your page appears in search results for that language.'
                : 'Your page doesn\'t specify its language. Adding a language attribute helps search engines understand your target audience and can improve your page\'s visibility in search results for that language.'}
          />
        </div>
      </section>

      {/* Heading Structure Analysis */}
      <section className="result-section">
        <div className="section-header comparison-row">
          <div className="comparison-metric-column">
            <h3 className="section-title">Heading Structure Analysis</h3>
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateHeadingScore(our.headings)} />
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateHeadingScore(competitor.headings)} />
          </div>
        </div>
        <div className="section-content">
          <ComparisonRow
            label="H1 Heading"
            ourValue={our.headings.h1Count === 0 ? 'Not found' : `${our.headings.h1Count} detected`}
            competitorValue={competitor.headings.h1Count === 0 ? 'Not found' : `${competitor.headings.h1Count} detected`}
            ourStatus={our.headings.h1Count === 1 ? 'good' : our.headings.h1Count === 0 ? 'error' : 'warning'}
            competitorStatus={competitor.headings.h1Count === 1 ? 'good' : competitor.headings.h1Count === 0 ? 'error' : 'warning'}
            ourRemark={
              our.headings.h1Count === 0
                ? 'Your page is missing the main heading (H1 tag). This is the most important heading that tells both visitors and search engines what your page is about. Add one clear, descriptive H1 at the top of your main content.'
                : our.headings.h1Count > 1
                ? `You have ${our.headings.h1Count} main headings (H1 tags) on this page. It's best to have only one H1 per page to clearly show what the page is about. Remove the extra ones and use H2 tags for other important sections instead.`
                : 'Your page has one main heading (H1 tag), which is perfect. This tells both visitors and search engines what your page is about. Make sure it clearly describes your page content.'}
            competitorRemark={
              competitor.headings.h1Count === 0
                ? 'Your page is missing the main heading (H1 tag). This is the most important heading that tells both visitors and search engines what your page is about. Add one clear, descriptive H1 at the top of your main content.'
                : competitor.headings.h1Count > 1
                ? `You have ${competitor.headings.h1Count} main headings (H1 tags) on this page. It's best to have only one H1 per page to clearly show what the page is about. Remove the extra ones and use H2 tags for other important sections instead.`
                : 'Your page has one main heading (H1 tag), which is perfect. This tells both visitors and search engines what your page is about. Make sure it clearly describes your page content.'}
            ourAdditionalContent={
              our.headings.h1.length > 0 ? (
                <div>
                  <div className="additional-content-label">Detected H1 Headings:</div>
                  {formatHeadings(our.headings.h1)}
                </div>
              ) : null
            }
            competitorAdditionalContent={
              competitor.headings.h1.length > 0 ? (
                <div>
                  <div className="additional-content-label">Detected H1 Headings:</div>
                  {formatHeadings(competitor.headings.h1)}
                </div>
              ) : null
            }
          />
          <ComparisonRow
            label="H2 Headings"
            ourValue={our.headings.h2.length === 0 ? 'Not found' : `${our.headings.h2.length} detected`}
            competitorValue={competitor.headings.h2.length === 0 ? 'Not found' : `${competitor.headings.h2.length} detected`}
            ourStatus={our.headings.h2.length > 0 ? 'good' : 'warning'}
            competitorStatus={competitor.headings.h2.length > 0 ? 'good' : 'warning'}
            ourRemark={
              our.headings.h2.length === 0
                ? 'Your page doesn\'t have any section headings (H2 tags). These help organize your content into clear sections, making it easier for both readers and search engines to understand your page structure. Add H2 tags to break up your content.'
                : `Your page has ${our.headings.h2.length} section headings (H2 tags), which helps organize your content into clear sections. This makes it easier for both readers and search engines to understand your page structure.`}
            competitorRemark={
              competitor.headings.h2.length === 0
                ? 'Your page doesn\'t have any section headings (H2 tags). These help organize your content into clear sections, making it easier for both readers and search engines to understand your page structure. Add H2 tags to break up your content.'
                : `Your page has ${competitor.headings.h2.length} section headings (H2 tags), which helps organize your content into clear sections. This makes it easier for both readers and search engines to understand your page structure.`}
            ourAdditionalContent={
              our.headings.h2.length > 0 ? (
                <div>
                  <div className="additional-content-label">Detected H2 Headings:</div>
                  {formatHeadings(our.headings.h2)}
                </div>
              ) : null
            }
            competitorAdditionalContent={
              competitor.headings.h2.length > 0 ? (
                <div>
                  <div className="additional-content-label">Detected H2 Headings:</div>
                  {formatHeadings(competitor.headings.h2)}
                </div>
              ) : null
            }
          />
          {(our.headings.h3.length > 0 || competitor.headings.h3.length > 0) && (
            <ComparisonRow
              label="H3 Headings"
              ourValue={our.headings.h3.length > 0 ? `${our.headings.h3.length} detected` : 'Not found'}
              competitorValue={competitor.headings.h3.length > 0 ? `${competitor.headings.h3.length} detected` : 'Not found'}
              ourStatus={our.headings.h3.length > 0 ? 'good' : null}
              competitorStatus={competitor.headings.h3.length > 0 ? 'good' : null}
              ourRemark={our.headings.h3.length > 0 ? `Your page has ${our.headings.h3.length} subsection headings (H3 tags), which further organize your content under H2 sections. This creates a clear hierarchy that helps both readers and search engines understand your content structure.` : null}
              competitorRemark={competitor.headings.h3.length > 0 ? `Your page has ${competitor.headings.h3.length} subsection headings (H3 tags), which further organize your content under H2 sections. This creates a clear hierarchy that helps both readers and search engines understand your content structure.` : null}
              ourAdditionalContent={
                our.headings.h3.length > 0 ? (
                  <div>
                    <div className="additional-content-label">Detected H3 Headings:</div>
                    {formatHeadings(our.headings.h3)}
                  </div>
                ) : null
              }
              competitorAdditionalContent={
                competitor.headings.h3.length > 0 ? (
                  <div>
                    <div className="additional-content-label">Detected H3 Headings:</div>
                    {formatHeadings(competitor.headings.h3)}
                  </div>
                ) : null
              }
            />
          )}
        </div>
      </section>

      {/* Keyword Visibility Analysis */}
      <section className="result-section">
        <div className="section-header comparison-row">
          <div className="comparison-metric-column">
            <h3 className="section-title">Keyword Visibility Analysis</h3>
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateKeywordScore(our.keywords)} />
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateKeywordScore(competitor.keywords)} />
          </div>
        </div>
        <div className="section-content">
          <ComparisonRow
            label="Meta Keywords"
            ourValue={our.keywords.metaKeywords || 'Not provided'}
            competitorValue={competitor.keywords.metaKeywords || 'Not provided'}
            ourStatus="good"
            competitorStatus="good"
            ourRemark="Meta keywords are largely ignored by search engines. Focus on on-page usage instead."
            competitorRemark="Meta keywords are largely ignored by search engines. Focus on on-page usage instead."
          />
          <ComparisonRow
            label="Top Keywords"
            ourValue={
              our.keywords.topKeywords.length
                ? (
                  <div className="keyword-stack">
                    {our.keywords.topKeywords.slice(0, 10).map((kw, idx) => (
                      <span key={idx} className="keyword-chip">
                        {kw.word} <span>{kw.count}×</span>
                      </span>
                    ))}
                  </div>
                )
                : null
            }
            competitorValue={
              competitor.keywords.topKeywords.length
                ? (
                  <div className="keyword-stack">
                    {competitor.keywords.topKeywords.slice(0, 10).map((kw, idx) => (
                      <span key={idx} className="keyword-chip">
                        {kw.word} <span>{kw.count}×</span>
                      </span>
                    ))}
                  </div>
                )
                : null
            }
            ourStatus={our.keywords.topKeywords.length ? 'good' : 'warning'}
            competitorStatus={competitor.keywords.topKeywords.length ? 'good' : 'warning'}
            ourRemark={getKeywordRemark(our.keywords)}
            competitorRemark={getKeywordRemark(competitor.keywords)}
          />
        </div>
      </section>

      {/* Content Quality Analysis */}
      <section className="result-section">
        <div className="section-header comparison-row">
          <div className="comparison-metric-column">
            <h3 className="section-title">Content Quality Analysis</h3>
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateContentScore(our.content)} />
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateContentScore(competitor.content)} />
          </div>
        </div>
        <div className="section-content">
          {(() => {
            const ourContentRemark = getContentRemark(our.content);
            const competitorContentRemark = getContentRemark(competitor.content);
            return (
              <>
                <ComparisonRow
                  label="Word Count"
                  ourValue={`${our.content.wordCount.toLocaleString()} words`}
                  competitorValue={`${competitor.content.wordCount.toLocaleString()} words`}
                  ourStatus={ourContentRemark.status}
                  competitorStatus={competitorContentRemark.status}
                  ourRemark={ourContentRemark.remark}
                  competitorRemark={competitorContentRemark.remark}
                />
                <ComparisonRow
                  label="Paragraphs"
                  ourValue={`${our.content.paragraphCount} paragraphs`}
                  competitorValue={`${competitor.content.paragraphCount} paragraphs`}
                  ourStatus="good"
                  competitorStatus="good"
                  ourRemark="Break content into short paragraphs to keep readers engaged."
                  competitorRemark="Break content into short paragraphs to keep readers engaged."
                />
                {(our.content.readability || competitor.content.readability) && (
                  <ComparisonRow
                    label="Readability"
                    ourValue={
                      our.content.readability ? (
                        <div className="readability-metrics">
                          <span>{our.content.readability.sentences} sentences</span>
                          <span>{our.content.readability.avgWordsPerSentence} words/sentence</span>
                          <span>{our.content.readability.avgCharsPerWord} chars/word</span>
                        </div>
                      ) : null
                    }
                    competitorValue={
                      competitor.content.readability ? (
                        <div className="readability-metrics">
                          <span>{competitor.content.readability.sentences} sentences</span>
                          <span>{competitor.content.readability.avgWordsPerSentence} words/sentence</span>
                          <span>{competitor.content.readability.avgCharsPerWord} chars/word</span>
                        </div>
                      ) : null
                    }
                    ourStatus="good"
                    competitorStatus="good"
                    ourRemark="Aim for 15–20 words per sentence and 4–6 characters per word for scannability."
                    competitorRemark="Aim for 15–20 words per sentence and 4–6 characters per word for scannability."
                  />
                )}
              </>
            );
          })()}
        </div>
      </section>

      {/* Image Hygiene Analysis */}
      <section className="result-section">
        <div className="section-header comparison-row">
          <div className="comparison-metric-column">
            <h3 className="section-title">Image Hygiene Analysis</h3>
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateImageScore(our.images)} />
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateImageScore(competitor.images)} />
          </div>
        </div>
        <div className="section-content">
          {(() => {
            // eslint-disable-next-line no-unused-vars
            const ourImageRemark = getImageRemark(our.images);
            // eslint-disable-next-line no-unused-vars
            const competitorImageRemark = getImageRemark(competitor.images);
            return (
              <>
                <ComparisonRow
                  label="Total Images"
                  ourValue={`${our.images.total} image${our.images.total === 1 ? '' : 's'}`}
                  competitorValue={`${competitor.images.total} image${competitor.images.total === 1 ? '' : 's'}`}
                  ourStatus="good"
                  competitorStatus="good"
                  ourRemark="The total number of images found on your page. Compress assets and lazy load lower-fold visuals to improve page speed."
                  competitorRemark="The total number of images found on your page. Compress assets and lazy load lower-fold visuals to improve page speed."
                />
                <ComparisonRow
                  label="Images Missing Alt Text"
                  ourValue={our.images.withoutAlt === 0 ? 'All images have alt text' : `${our.images.withoutAlt} image${our.images.withoutAlt === 1 ? '' : 's'} missing alt text`}
                  competitorValue={competitor.images.withoutAlt === 0 ? 'All images have alt text' : `${competitor.images.withoutAlt} image${competitor.images.withoutAlt === 1 ? '' : 's'} missing alt text`}
                  ourStatus={our.images.withoutAlt === 0 ? 'good' : 'warning'}
                  competitorStatus={competitor.images.withoutAlt === 0 ? 'good' : 'warning'}
                  ourRemark={
                    our.images.withoutAlt === 0
                      ? 'All your images have alt text, which is great! Alt text helps screen readers describe images to visually impaired users and provides context to search engines about what your images show.'
                      : `You have ${our.images.withoutAlt} image${our.images.withoutAlt === 1 ? '' : 's'} without alt text. Alt text is important for accessibility and SEO—it helps screen readers describe images to visually impaired users and tells search engines what your images contain. Add descriptive alt text to all images.`}
                  competitorRemark={
                    competitor.images.withoutAlt === 0
                      ? 'All your images have alt text, which is great! Alt text helps screen readers describe images to visually impaired users and provides context to search engines about what your images show.'
                      : `You have ${competitor.images.withoutAlt} image${competitor.images.withoutAlt === 1 ? '' : 's'} without alt text. Alt text is important for accessibility and SEO—it helps screen readers describe images to visually impaired users and tells search engines what your images contain. Add descriptive alt text to all images.`}
                  ourAdditionalContent={
                    our.images.missingAltList && our.images.missingAltList.length > 0 ? (
                      <div className="image-detail-row image-detail-warning">
                        <div className="image-detail-content">
                          {formatImageThumbnails(our.images.missingAltList, {
                            remarkText: 'Add descriptive alt text to this image for better accessibility and SEO.'
                          })}
                        </div>
                      </div>
                    ) : null
                  }
                  competitorAdditionalContent={
                    competitor.images.missingAltList && competitor.images.missingAltList.length > 0 ? (
                      <div className="image-detail-row image-detail-warning">
                        <div className="image-detail-content">
                          {formatImageThumbnails(competitor.images.missingAltList, {
                            remarkText: 'Add descriptive alt text to this image for better accessibility and SEO.'
                          })}
                        </div>
                      </div>
                    ) : null
                  }
                />
                <ComparisonRow
                  label="Large Images"
                  ourValue={our.images.largeImages === 0 ? 'No large images found' : `${our.images.largeImages} image${our.images.largeImages === 1 ? '' : 's'} larger than 300KB`}
                  competitorValue={competitor.images.largeImages === 0 ? 'No large images found' : `${competitor.images.largeImages} image${competitor.images.largeImages === 1 ? '' : 's'} larger than 300KB`}
                  ourStatus={our.images.largeImages === 0 ? 'good' : 'warning'}
                  competitorStatus={competitor.images.largeImages === 0 ? 'good' : 'warning'}
                  ourRemark={
                    our.images.largeImages === 0
                      ? 'All your images are optimized and under 300KB, which is great for page speed. Large images can slow down your website, especially on mobile devices.'
                      : `You have ${our.images.largeImages} image${our.images.largeImages === 1 ? '' : 's'} larger than 300KB. Large images can significantly slow down your website, especially on mobile devices. Compress these images or convert them to more efficient formats like WebP to improve page load times.`}
                  competitorRemark={
                    competitor.images.largeImages === 0
                      ? 'All your images are optimized and under 300KB, which is great for page speed. Large images can slow down your website, especially on mobile devices.'
                      : `You have ${competitor.images.largeImages} image${competitor.images.largeImages === 1 ? '' : 's'} larger than 300KB. Large images can significantly slow down your website, especially on mobile devices. Compress these images or convert them to more efficient formats like WebP to improve page load times.`}
                  ourAdditionalContent={
                    our.images.largeImageList && our.images.largeImageList.length > 0 ? (
                      <div className="image-detail-row image-detail-warning">
                        <div className="image-detail-content">
                          {formatImageThumbnails(our.images.largeImageList, {
                            showSize: true,
                            remarkText: 'Compress this image below 300KB or convert to WebP/AVIF to speed up page loads.'
                          })}
                        </div>
                      </div>
                    ) : null
                  }
                  competitorAdditionalContent={
                    competitor.images.largeImageList && competitor.images.largeImageList.length > 0 ? (
                      <div className="image-detail-row image-detail-warning">
                        <div className="image-detail-content">
                          {formatImageThumbnails(competitor.images.largeImageList, {
                            showSize: true,
                            remarkText: 'Compress this image below 300KB or convert to WebP/AVIF to speed up page loads.'
                          })}
                        </div>
                      </div>
                    ) : null
                  }
                />
                <ComparisonRow
                  label="Images Missing WebP Version"
                  ourValue={our.images.missingWebpList && our.images.missingWebpList.length === 0 ? 'All images have WebP versions' : `${our.images.missingWebpList?.length || 0} image${(our.images.missingWebpList?.length || 0) === 1 ? '' : 's'} missing WebP version`}
                  competitorValue={competitor.images.missingWebpList && competitor.images.missingWebpList.length === 0 ? 'All images have WebP versions' : `${competitor.images.missingWebpList?.length || 0} image${(competitor.images.missingWebpList?.length || 0) === 1 ? '' : 's'} missing WebP version`}
                  ourStatus={our.images.missingWebpList && our.images.missingWebpList.length === 0 ? 'good' : 'warning'}
                  competitorStatus={competitor.images.missingWebpList && competitor.images.missingWebpList.length === 0 ? 'good' : 'warning'}
                  ourRemark={
                    our.images.missingWebpList && our.images.missingWebpList.length === 0
                      ? 'All your images have WebP versions available, which is excellent! WebP is a modern image format that provides better compression than JPEG or PNG, resulting in faster page loads.'
                      : `You have ${our.images.missingWebpList?.length || 0} image${(our.images.missingWebpList?.length || 0) === 1 ? '' : 's'} without WebP versions. WebP is a modern image format that provides better compression than JPEG or PNG, typically reducing file sizes by 25-35% while maintaining quality. Convert your images to WebP format to improve page load times.`}
                  competitorRemark={
                    competitor.images.missingWebpList && competitor.images.missingWebpList.length === 0
                      ? 'All your images have WebP versions available, which is excellent! WebP is a modern image format that provides better compression than JPEG or PNG, resulting in faster page loads.'
                      : `You have ${competitor.images.missingWebpList?.length || 0} image${(competitor.images.missingWebpList?.length || 0) === 1 ? '' : 's'} without WebP versions. WebP is a modern image format that provides better compression than JPEG or PNG, typically reducing file sizes by 25-35% while maintaining quality. Convert your images to WebP format to improve page load times.`}
                  ourAdditionalContent={
                    our.images.missingWebpList && our.images.missingWebpList.length > 0 ? (
                      <div className="image-detail-row image-detail-warning">
                        <div className="image-detail-content">
                          {formatImageThumbnails(our.images.missingWebpList, {
                            remarkText: 'Convert this image to WebP format to reduce file size and improve page load times. WebP typically reduces file sizes by 25-35% while maintaining quality.'
                          })}
                        </div>
                      </div>
                    ) : null
                  }
                  competitorAdditionalContent={
                    competitor.images.missingWebpList && competitor.images.missingWebpList.length > 0 ? (
                      <div className="image-detail-row image-detail-warning">
                        <div className="image-detail-content">
                          {formatImageThumbnails(competitor.images.missingWebpList, {
                            remarkText: 'Convert this image to WebP format to reduce file size and improve page load times. WebP typically reduces file sizes by 25-35% while maintaining quality.'
                          })}
                        </div>
                      </div>
                    ) : null
                  }
                />
              </>
            );
          })()}
        </div>
      </section>

      {/* Link Profile Analysis */}
      <section className="result-section">
        <div className="section-header comparison-row">
          <div className="comparison-metric-column">
            <h3 className="section-title">Link Profile Analysis</h3>
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateLinkScore(our.links)} />
          </div>
          <div className="comparison-value-column">
            <CircularProgress percentage={calculateLinkScore(competitor.links)} />
          </div>
        </div>
        <div className="section-content">
          {(() => {
            const ourLinkRemark = getLinkRemark(our.links);
            const competitorLinkRemark = getLinkRemark(competitor.links);
            return (
              <>
                <ComparisonRow
                  label="Canonical URL"
                  ourValue={
                    our.links.canonicalUrl ? (
                      <a
                        href={our.links.canonicalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-link"
                      >
                        {our.links.canonicalUrl}
                      </a>
                    ) : 'Not set'
                  }
                  competitorValue={
                    competitor.links.canonicalUrl ? (
                      <a
                        href={competitor.links.canonicalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-link"
                      >
                        {competitor.links.canonicalUrl}
                      </a>
                    ) : 'Not set'
                  }
                  ourStatus={our.links.canonicalUrl ? 'good' : 'warning'}
                  competitorStatus={competitor.links.canonicalUrl ? 'good' : 'warning'}
                  ourRemark={
                    our.links.canonicalUrl
                      ? 'Great — your canonical tag clearly tells search engines which URL is the master copy of this page. Make sure it always points to the preferred clean URL.'
                      : 'You haven\'t set a canonical tag. Add <link rel="canonical"> so search engines don\'t treat URL variations as duplicate content.'}
                  competitorRemark={
                    competitor.links.canonicalUrl
                      ? 'Great — your canonical tag clearly tells search engines which URL is the master copy of this page. Make sure it always points to the preferred clean URL.'
                      : 'You haven\'t set a canonical tag. Add <link rel="canonical"> so search engines don\'t treat URL variations as duplicate content.'}
                />
                <ComparisonRow
                  label="Total Links"
                  ourValue={`${our.links.total} links (${our.links.internal} internal / ${our.links.external} external)`}
                  competitorValue={`${competitor.links.total} links (${competitor.links.internal} internal / ${competitor.links.external} external)`}
                  ourStatus={ourLinkRemark.status}
                  competitorStatus={competitorLinkRemark.status}
                  ourRemark={ourLinkRemark.remark}
                  competitorRemark={competitorLinkRemark.remark}
                />
                <ComparisonRow
                  label="Internal Links"
                  ourValue={`${our.links.internal} internal link${our.links.internal === 1 ? '' : 's'}`}
                  competitorValue={`${competitor.links.internal} internal link${competitor.links.internal === 1 ? '' : 's'}`}
                  ourStatus={our.links.internal > 0 ? 'good' : 'warning'}
                  competitorStatus={competitor.links.internal > 0 ? 'good' : 'warning'}
                  ourRemark={
                    our.links.internal > 0
                      ? 'Internal links help visitors and crawlers discover more of your content. Keep adding contextual links that guide people deeper into the site.'
                      : 'No internal links were detected on this page. Link to related pages to strengthen site structure and pass authority between key URLs.'}
                  competitorRemark={
                    competitor.links.internal > 0
                      ? 'Internal links help visitors and crawlers discover more of your content. Keep adding contextual links that guide people deeper into the site.'
                      : 'No internal links were detected on this page. Link to related pages to strengthen site structure and pass authority between key URLs.'}
                  ourAdditionalContent={
                    our.links.internalList && our.links.internalList.length > 0 ? (
                      <div className="link-detail-row">
                        <div className="link-detail-content">
                          {formatLinkCards(our.links.internalList)}
                        </div>
                      </div>
                    ) : null
                  }
                  competitorAdditionalContent={
                    competitor.links.internalList && competitor.links.internalList.length > 0 ? (
                      <div className="link-detail-row">
                        <div className="link-detail-content">
                          {formatLinkCards(competitor.links.internalList)}
                        </div>
                      </div>
                    ) : null
                  }
                />
                <ComparisonRow
                  label="External Links"
                  ourValue={`${our.links.external} external link${our.links.external === 1 ? '' : 's'}`}
                  competitorValue={`${competitor.links.external} external link${competitor.links.external === 1 ? '' : 's'}`}
                  ourStatus={our.links.external > 0 ? 'good' : 'warning'}
                  competitorStatus={competitor.links.external > 0 ? 'good' : 'warning'}
                  ourRemark={
                    our.links.external > 0
                      ? 'External links to reputable sources boost credibility. Make sure they open in a new tab and use rel="nofollow" where appropriate.'
                      : 'You don\'t link out to other trusted resources on this page. Consider citing helpful references or partners when it adds value.'}
                  competitorRemark={
                    competitor.links.external > 0
                      ? 'External links to reputable sources boost credibility. Make sure they open in a new tab and use rel="nofollow" where appropriate.'
                      : 'You don\'t link out to other trusted resources on this page. Consider citing helpful references or partners when it adds value.'}
                  ourAdditionalContent={
                    our.links.externalList && our.links.externalList.length > 0 ? (
                      <div className="link-detail-row">
                        <div className="link-detail-content">
                          {formatLinkCards(our.links.externalList)}
                        </div>
                      </div>
                    ) : null
                  }
                  competitorAdditionalContent={
                    competitor.links.externalList && competitor.links.externalList.length > 0 ? (
                      <div className="link-detail-row">
                        <div className="link-detail-content">
                          {formatLinkCards(competitor.links.externalList)}
                        </div>
                      </div>
                    ) : null
                  }
                />
                <ComparisonRow
                  label="Broken Links"
                  ourValue={
                    our.links.broken && our.links.broken.length
                      ? `${our.links.broken.length} broken link${our.links.broken.length === 1 ? '' : 's'}`
                      : 'No broken links detected'
                  }
                  competitorValue={
                    competitor.links.broken && competitor.links.broken.length
                      ? `${competitor.links.broken.length} broken link${competitor.links.broken.length === 1 ? '' : 's'}`
                      : 'No broken links detected'
                  }
                  ourStatus={our.links.broken && our.links.broken.length ? 'error' : 'good'}
                  competitorStatus={competitor.links.broken && competitor.links.broken.length ? 'error' : 'good'}
                  ourRemark={
                    our.links.broken && our.links.broken.length
                      ? 'Some links return 4xx/5xx errors or time out. Fix or remove them so users and crawlers don\'t hit dead ends.'
                      : 'All checked links responded successfully. Keep monitoring regularly to catch future issues.'}
                  competitorRemark={
                    competitor.links.broken && competitor.links.broken.length
                      ? 'Some links return 4xx/5xx errors or time out. Fix or remove them so users and crawlers don\'t hit dead ends.'
                      : 'All checked links responded successfully. Keep monitoring regularly to catch future issues.'}
                  ourAdditionalContent={
                    our.links.broken && our.links.broken.length > 0 ? (
                      <div className="link-detail-row link-detail-warning">
                        <div className="link-detail-content">
                          {formatLinkCards(
                            our.links.broken.map((link) => ({
                              ...link,
                              tagLabelOverride: 'Broken Link',
                              tagClassOverride: 'tag-broken'
                            })),
                            {
                              remarkText: 'This URL seems to be a broken URL. Kindly check manually.'
                            }
                          )}
                        </div>
                      </div>
                    ) : null
                  }
                  competitorAdditionalContent={
                    competitor.links.broken && competitor.links.broken.length > 0 ? (
                      <div className="link-detail-row link-detail-warning">
                        <div className="link-detail-content">
                          {formatLinkCards(
                            competitor.links.broken.map((link) => ({
                              ...link,
                              tagLabelOverride: 'Broken Link',
                              tagClassOverride: 'tag-broken'
                            })),
                            {
                              remarkText: 'This URL seems to be a broken URL. Kindly check manually.'
                            }
                          )}
                        </div>
                      </div>
                    ) : null
                  }
                />
                <ComparisonRow
                  label="NoFollow Links"
                  ourValue={`${our.links.nofollow || 0} tagged as nofollow`}
                  competitorValue={`${competitor.links.nofollow || 0} tagged as nofollow`}
                  ourStatus="good"
                  competitorStatus="good"
                  ourRemark="Use rel='nofollow' on untrusted or paid links to stay compliant."
                  competitorRemark="Use rel='nofollow' on untrusted or paid links to stay compliant."
                />
              </>
            );
          })()}
        </div>
      </section>
    </>
  );
};

const CompetitorAnalysis = ({ user, onLogout }) => {
  const { userPlan, currentProject } = useProject();
  const [targetUrl, setTargetUrl] = useState('');
  const [competitorUrl, setCompetitorUrl] = useState('');
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [competitors, setCompetitors] = useState([]); // Array of { id, targetUrl, competitorUrl, keyword, ourResults, competitorResults, timestamp }
  const [selectedCompetitorId, setSelectedCompetitorId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingCompetitorId, setDeletingCompetitorId] = useState(null);
  const [deletingCompetitorName, setDeletingCompetitorName] = useState('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [theme, setTheme] = useState('light');
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [funFact, setFunFact] = useState('');
  const [analyzeCountdown, setAnalyzeCountdown] = useState(0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Helper function to get countdown key for analyze action
  const getAnalyzeCountdownKey = (projectUrl) => {
    if (!projectUrl) return null;
    const normalizedUrl = projectUrl.toLowerCase().trim().replace(/\/$/, '');
    return `competitorAnalysis_countdown_analyze_${normalizedUrl}`;
  };

  // Load theme on component mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Load saved competitors on component mount
  useEffect(() => {
    try {
      const savedData = localStorage.getItem('competitorAnalyses');
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setCompetitors(parsed);
          // Select the first competitor if available and none is selected
          setSelectedCompetitorId(prev => prev || parsed[0].id);
        }
      }
    } catch (err) {
      console.error('Error loading saved competitor analyses:', err);
    } finally {
      setIsInitialLoad(false);
    }
  }, []);

  // Check analyze countdown on mount and when project/targetUrl changes
  useEffect(() => {
    const targetUrlForCountdown = currentProject?.url || targetUrl || null;
    if (!targetUrlForCountdown) {
      setAnalyzeCountdown(0);
      return;
    }

    const analyzeKey = getAnalyzeCountdownKey(targetUrlForCountdown);
    if (!analyzeKey) {
      setAnalyzeCountdown(0);
      return;
    }

    const savedCountdownEnd = localStorage.getItem(analyzeKey);
    if (savedCountdownEnd) {
      const endTime = parseInt(savedCountdownEnd, 10);
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((endTime - now) / 1000));
      
      if (remaining > 0) {
        setAnalyzeCountdown(remaining);
      } else {
        localStorage.removeItem(analyzeKey);
        setAnalyzeCountdown(0);
      }
    } else {
      setAnalyzeCountdown(0);
    }
  }, [currentProject?.url, targetUrl]);

  // Analyze countdown timer effect
  useEffect(() => {
    if (analyzeCountdown <= 0) return;
    
    const targetUrlForCountdown = currentProject?.url || targetUrl || null;
    const analyzeKey = getAnalyzeCountdownKey(targetUrlForCountdown);
    if (!analyzeKey) return;

    const timer = setInterval(() => {
      setAnalyzeCountdown((prev) => {
        if (prev <= 1) {
          localStorage.removeItem(analyzeKey);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [analyzeCountdown, currentProject?.url, targetUrl]);

  // eslint-disable-next-line no-unused-vars
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  // Save competitors to localStorage whenever they change (but not during initial load)
  useEffect(() => {
    if (!isInitialLoad && competitors.length > 0) {
      try {
        localStorage.setItem('competitorAnalyses', JSON.stringify(competitors));
      } catch (err) {
        console.error('Error saving competitor analyses:', err);
      }
    }
  }, [competitors, isInitialLoad]);

  const handleCloseModal = () => {
    setShowAddModal(false);
    setTargetUrl('');
    setCompetitorUrl('');
    setKeyword('');
    setError(null);
  };

  const handleDeleteCompetitor = (competitorId) => {
    const competitor = competitors.find(c => c.id === competitorId);
    if (competitor) {
      setDeletingCompetitorId(competitorId);
      setDeletingCompetitorName(competitor.competitorUrl);
      setShowDeleteConfirm(true);
    }
  };

  const handleConfirmDelete = () => {
    if (!deletingCompetitorId) return;
    
    const updatedCompetitors = competitors.filter(c => c.id !== deletingCompetitorId);
    setCompetitors(updatedCompetitors);
    
    // If deleted competitor was selected, select another one or clear selection
    if (selectedCompetitorId === deletingCompetitorId) {
      if (updatedCompetitors.length > 0) {
        setSelectedCompetitorId(updatedCompetitors[0].id);
      } else {
        setSelectedCompetitorId(null);
      }
    }
    
    // Save to localStorage immediately
    try {
      if (updatedCompetitors.length > 0) {
        localStorage.setItem('competitorAnalyses', JSON.stringify(updatedCompetitors));
      } else {
        localStorage.removeItem('competitorAnalyses');
      }
    } catch (err) {
      console.error('Error saving competitor analyses after deletion:', err);
    }
    
    setShowDeleteConfirm(false);
    setDeletingCompetitorId(null);
    setDeletingCompetitorName('');
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeletingCompetitorId(null);
    setDeletingCompetitorName('');
  };

  const handleReAnalyze = async (competitorId) => {
    const competitor = competitors.find(c => c.id === competitorId);
    if (!competitor) return;

    // Prevent analyze during countdown
    if (analyzeCountdown > 0) return;

    // Show full-page loading screen
    const randomFact = funFacts[Math.floor(Math.random() * funFacts.length)];
    setFunFact(randomFact);
    setLoadingMessage('Re-analyzing competitor...');
    setShowLoadingScreen(true);
    setLoading(true);
    setError(null);

    try {
      // Get URLs from the competitor data
      const targetUrlToAnalyze = competitor.ourResults?.url || competitor.ourResults?.targetUrl || competitor.targetUrl;
      const competitorUrlToAnalyze = competitor.competitorResults?.url || competitor.competitorResults?.targetUrl || competitor.competitorUrl;
      const keywordToUse = competitor.keyword || '';

      // Validate and format URLs
      let formattedTargetUrl = targetUrlToAnalyze.trim();
      if (!formattedTargetUrl.startsWith('http://') && !formattedTargetUrl.startsWith('https://')) {
        formattedTargetUrl = 'https://' + formattedTargetUrl;
      }

      let formattedCompetitorUrl = competitorUrlToAnalyze.trim();
      if (!formattedCompetitorUrl.startsWith('http://') && !formattedCompetitorUrl.startsWith('https://')) {
        formattedCompetitorUrl = 'https://' + formattedCompetitorUrl;
      }

      // Analyze our URL first
      const targetResponse = await axios.post(getApiUrl('analyze'), { url: formattedTargetUrl }, axiosConfig);
      const targetData = targetResponse.data;
      
      // Set our results
      const keywordLower = keywordToUse.trim().toLowerCase();
      let finalOurResults;
      if (keywordLower) {
        finalOurResults = extractKeywordData(targetData, keywordLower, formattedTargetUrl);
      } else {
        finalOurResults = { auditResults: targetData, url: formattedTargetUrl };
      }

      // Analyze competitor URL
      const competitorResponse = await axios.post(getApiUrl('analyze'), { url: formattedCompetitorUrl }, axiosConfig);
      const competitorData = competitorResponse.data;
      
      // Set competitor results
      let finalCompetitorResults;
      if (keywordLower) {
        finalCompetitorResults = extractKeywordData(competitorData, keywordLower, formattedCompetitorUrl);
      } else {
        finalCompetitorResults = { auditResults: competitorData, url: formattedCompetitorUrl };
      }

      // Update the competitor in the array
      const updatedCompetitors = competitors.map(c => 
        c.id === competitorId 
          ? { 
              ...c, 
              ourResults: finalOurResults, 
              competitorResults: finalCompetitorResults,
              timestamp: new Date().toISOString()
            }
          : c
      );
      setCompetitors(updatedCompetitors);

      // Start 2-minute countdown (120 seconds) for analyze action
      const targetUrlForCountdown = currentProject?.url || targetUrl || competitor.targetUrl;
      const analyzeKey = getAnalyzeCountdownKey(targetUrlForCountdown);
      if (analyzeKey) {
        const countdownEndTime = Date.now() + (2 * 60 * 1000);
        localStorage.setItem(analyzeKey, countdownEndTime.toString());
        setAnalyzeCountdown(120);
      }

    } catch (err) {
      console.error('Re-analyze competitor error:', err);
      let errorMessage = 'Failed to re-analyze competitor. Please try again.';
      
      if (err.response) {
        errorMessage = err.response.data?.error || `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Unable to connect to server. Please make sure the backend server is running on port 5001.';
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      setShowLoadingScreen(false);
    }
  };

  // Helper function to get competitor limit based on plan
  const getCompetitorLimit = (plan) => {
    const normalizedPlan = (plan || 'basic').toLowerCase();
    
    // Lifetime plan has unlimited competitors
    if (normalizedPlan === 'lifetime') {
      return null; // null means unlimited
    }
    
    // Standard plan limits
    const planLimits = {
      'basic': 0,        // No competitors for basic plan
      'individual': 5,
      'business': 10,
      'enterprise': 50,
      'custom': 50
    };
    
    return planLimits[normalizedPlan] ?? 0; // Default to 0 if plan not recognized
  };

  const handleAnalyze = async (e) => {
    e.preventDefault();
    
    if (!targetUrl.trim()) {
      setError('Please enter your target URL');
      return;
    }

    if (!competitorUrl.trim()) {
      setError('Please enter competitor URL');
      return;
    }

    // Check competitor limit based on user plan
    const competitorLimit = getCompetitorLimit(userPlan);
    if (competitorLimit !== null && competitors.length >= competitorLimit) {
      const normalizedPlan = (userPlan || 'basic').toLowerCase();
      if (normalizedPlan === 'basic') {
        setError('Competitor Analysis is not available for Basic plan. Please upgrade to add competitors.');
      } else {
        setError(`${normalizedPlan.charAt(0).toUpperCase() + normalizedPlan.slice(1)} plan allows up to ${competitorLimit} competitors. Please upgrade to add more.`);
      }
      setShowAddModal(false);
      // Show upgrade modal if available
      return;
    }

    // Show full-page loading screen
    const randomFact = funFacts[Math.floor(Math.random() * funFacts.length)];
    setFunFact(randomFact);
    setLoadingMessage('Analyzing competitors...');
    setShowLoadingScreen(true);

    setLoading(true);
    setError(null);

    try {
      // Validate and format URLs
      let formattedTargetUrl = targetUrl.trim();
      if (!formattedTargetUrl.startsWith('http://') && !formattedTargetUrl.startsWith('https://')) {
        formattedTargetUrl = 'https://' + formattedTargetUrl;
      }

      let formattedCompetitorUrl = competitorUrl.trim();
      if (!formattedCompetitorUrl.startsWith('http://') && !formattedCompetitorUrl.startsWith('https://')) {
        formattedCompetitorUrl = 'https://' + formattedCompetitorUrl;
      }

      // Validate competitor URL format
      let competitorUrlObj;
      try {
        competitorUrlObj = new URL(formattedCompetitorUrl);
      } catch (e) {
        setError('Please enter a valid competitor URL');
        setLoading(false);
        setShowLoadingScreen(false);
        return;
      }

      // Validate competitor URL has a valid domain (not just a protocol)
      const competitorDomain = competitorUrlObj.hostname.toLowerCase();
      if (!competitorDomain || competitorDomain === '') {
        setError('Please enter a valid competitor URL with a domain name');
        setLoading(false);
        setShowLoadingScreen(false);
        return;
      }

      // Validate that the target URL belongs to the currently selected project
      if (!currentProject || !currentProject.url) {
        setError('Please select a project first.');
        setLoading(false);
        setShowLoadingScreen(false);
        return;
      }

      // Validate target URL domain matches the selected project
      let targetUrlObj;
      try {
        targetUrlObj = new URL(formattedTargetUrl);
      } catch (e) {
        setError('Please enter a valid target URL');
        setLoading(false);
        setShowLoadingScreen(false);
        return;
      }

      const targetDomain = targetUrlObj.hostname.toLowerCase();
      const targetOrigin = targetUrlObj.origin.toLowerCase();

      // Normalize project URL (add protocol if missing)
      let projectUrl = currentProject.url.trim();
      if (!projectUrl.startsWith('http://') && !projectUrl.startsWith('https://')) {
        projectUrl = 'https://' + projectUrl;
      }

      let belongsToProject = false;
      try {
        const projectUrlObj = new URL(projectUrl);
        const projectDomain = projectUrlObj.hostname.toLowerCase();
        const projectOrigin = projectUrlObj.origin.toLowerCase();
        
        // Check if domains match (allows subdomains)
        // For example: www.example.com matches example.com
        // But example.com does NOT match other-example.com
        belongsToProject = targetDomain === projectDomain || 
               targetDomain.endsWith('.' + projectDomain) ||
               projectDomain.endsWith('.' + targetDomain) ||
               targetOrigin === projectOrigin;
      } catch (e) {
        setError('Invalid project URL format.');
        setLoading(false);
        setShowLoadingScreen(false);
        return;
      }

      if (!belongsToProject) {
        setError(`The target URL must belong to the selected project domain (${currentProject.url}). Please use a URL from this project.`);
        setLoading(false);
        setShowLoadingScreen(false);
        return;
      }

      // Analyze our URL first
      const targetResponse = await axios.post(getApiUrl('analyze'), { url: formattedTargetUrl }, axiosConfig);
      const targetData = targetResponse.data;
      
      // Set our results first
      const keywordLower = keyword.trim().toLowerCase();
      let finalOurResults;
      if (keywordLower) {
        finalOurResults = extractKeywordData(targetData, keywordLower, formattedTargetUrl);
      } else {
        finalOurResults = { auditResults: targetData, url: formattedTargetUrl };
      }

      // Then analyze competitor URL
      const competitorResponse = await axios.post(getApiUrl('analyze'), { url: formattedCompetitorUrl }, axiosConfig);
      const competitorData = competitorResponse.data;

      // Process competitor URL
      let finalCompetitorResults;
      if (keywordLower) {
        finalCompetitorResults = extractKeywordData(competitorData, keywordLower, formattedCompetitorUrl);
      } else {
        finalCompetitorResults = { auditResults: competitorData, url: formattedCompetitorUrl };
      }

      // Create new competitor entry
      const newCompetitor = {
        id: Date.now().toString(),
        targetUrl: formattedTargetUrl,
        competitorUrl: formattedCompetitorUrl,
        keyword: keywordLower || '',
        ourResults: finalOurResults,
        competitorResults: finalCompetitorResults,
        timestamp: Date.now()
      };

      // Add to competitors list
      const updatedCompetitors = [...competitors, newCompetitor];
      setCompetitors(updatedCompetitors);
      setSelectedCompetitorId(newCompetitor.id);

      // Close modal after successful analysis
      handleCloseModal();
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.response?.data?.error || 'Failed to analyze URLs. Please try again.');
    } finally {
      setLoading(false);
      setShowLoadingScreen(false);
    }
  };

  // Extract keyword-specific data (similar to KeywordAnalysis)
  const extractKeywordData = (analysisData, keywordLower, url) => {
    // This is a simplified version - you'll need to implement the full keyword extraction logic
    // Similar to what's in KeywordAnalysis.js
    return {
      keyword: keywordLower,
      targetUrl: url,
      auditResults: analysisData,
      // Add more keyword-specific fields as needed
    };
  };

  // Basic plan lock: only pro plans can use Competitor Analysis
  const isBasicPlan = !userPlan || userPlan === 'basic';

  return (
    <div className="seo-analyzer competitor-analysis-page">
      {/* Full-page loading overlay similar to Dashboard/Site Audit */}
      {showLoadingScreen && (
        <div className="page-loader-overlay">
          <div className="page-loader-content">
            {funFact && (
              <div className="fun-fact-container">
                <div className="fun-fact-icon">💡</div>
                <p className="fun-fact-text">{funFact}</p>
              </div>
            )}
            <div className="page-loader-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <p className="page-loader-text">{loadingMessage}</p>
            <p className="page-loader-message">
              This will take a few seconds, depends on the website size. Kindly wait until our algorithms analyze your website.
            </p>
          </div>
        </div>
      )}
      <header className="header">
        <div className="header-container">
          <div className="header-actions">
            <a href="/pricing" className="header-menu-item">
              Plans & Pricing
            </a>
            {user && (
              <div className="user-info">
                <button
                  className="user-icon-button"
                  onClick={() => {
                    window.location.href = '/dashboard/account-settings';
                  }}
                  aria-label="Account settings"
                  title="Account settings"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <div className="container">
        {isBasicPlan ? (
          <div className="competitor-pro-lock">
            <div className="competitor-pro-lock-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="10" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>
            <div className="competitor-pro-lock-content">
              <h2 className="competitor-pro-lock-title">Competitor Analysis is a Pro feature</h2>
              <p className="competitor-pro-lock-text">
                Upgrade your plan to unlock competitor analysis 
                and side-by-side SEO insights for your website and competitors.
              </p>
              <button
                type="button"
                className="competitor-pro-lock-button"
                onClick={() => window.open('https://parrotseo.com/pricing', '_blank', 'noopener,noreferrer')}
              >
                Upgrade Plan
              </button>
            </div>
          </div>
        ) : (
        <div className="competitor-analysis-container">
          <div className="competitor-analysis-header">
            <h1 className="page-title">Competitor Analysis</h1>
            <button
              type="button"
              className="add-competitor-btn"
              onClick={() => {
                // Check competitor limit before opening modal
                const competitorLimit = getCompetitorLimit(userPlan);
                if (competitorLimit !== null && competitors.length >= competitorLimit) {
                  const normalizedPlan = (userPlan || 'basic').toLowerCase();
                  if (normalizedPlan === 'basic') {
                    setError('Competitor Analysis is not available for Basic plan. Please upgrade to add competitors.');
                  } else {
                    setError(`${normalizedPlan.charAt(0).toUpperCase() + normalizedPlan.slice(1)} plan allows up to ${competitorLimit} competitors. Please upgrade to add more.`);
                  }
                  setShowUpgradeModal(true);
                  return;
                }
                setShowAddModal(true);
              }}
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add Competitor
            </button>
          </div>

          <div className="competitors-section">
            <div className="competitors-box">
              {competitors.length === 0 ? (
                <div className="no-competitors-message">
                  <p>No competitors added yet. Click "Add Competitor" to get started.</p>
                </div>
              ) : (
                <div className="competitors-tags">
                  {competitors.map((competitor) => (
                    <div
                      key={competitor.id}
                      className={`competitor-tag ${selectedCompetitorId === competitor.id ? 'active' : ''}`}
                      onClick={() => setSelectedCompetitorId(competitor.id)}
                    >
                      <span className="competitor-tag-text">{competitor.competitorUrl}</span>
                      {competitor.keyword && (
                        <span className="competitor-tag-keyword">({competitor.keyword})</span>
                      )}
                      <button
                        className="competitor-tag-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCompetitor(competitor.id);
                        }}
                        title="Delete competitor"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {selectedCompetitorId && (() => {
            const selectedCompetitor = competitors.find(c => c.id === selectedCompetitorId);
            if (!selectedCompetitor) return null;

            return (
              <>
                <div className="competitor-results-header">
                  <div className="selected-competitor-info">
                    <div className="selected-competitor-row">
                      <span className="selected-competitor-label">Competitor:</span>
                      <span className="selected-competitor-url">{selectedCompetitor.competitorUrl}</span>
                      <button
                        type="button"
                        className="delete-competitor-header-btn"
                        onClick={() => handleDeleteCompetitor(selectedCompetitorId)}
                        title="Delete competitor"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                    {selectedCompetitor.keyword && (
                      <div className="selected-competitor-row">
                        <span className="selected-competitor-label">Keyword:</span>
                        <span className="selected-competitor-keyword">{selectedCompetitor.keyword}</span>
                      </div>
                    )}
                  </div>
                  <div className="competitor-results-actions">
                    <div className="last-analysis-info">
                      <div className="last-analysis-label">Last Analysis:</div>
                      <div className="last-analysis-date">
                        {new Date(selectedCompetitor.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="re-analyze-btn"
                      onClick={() => handleReAnalyze(selectedCompetitorId)}
                      disabled={loading || analyzeCountdown > 0}
                    >
                      {loading ? (
                        <>
                          <span className="spinner"></span>
                          Analyzing...
                        </>
                      ) : analyzeCountdown > 0 ? (
                        `RE-ANALYZE (${Math.floor(analyzeCountdown / 60)}:${String(analyzeCountdown % 60).padStart(2, '0')})`
                      ) : (
                        'RE-ANALYZE'
                      )}
                    </button>
                  </div>
                </div>

                <div className="comparison-container">
                <div className="comparison-header-row">
                  <div className="comparison-metric-column comparison-header">Metric</div>
                  <div className="comparison-value-column comparison-header">
                    <div className="column-title">Your Site</div>
                    <div className="column-url">{selectedCompetitor.ourResults?.url || selectedCompetitor.ourResults?.targetUrl || selectedCompetitor.targetUrl}</div>
                  </div>
                  <div className="comparison-value-column comparison-header">
                    <div className="column-title">Competitor</div>
                    <div className="column-url">{selectedCompetitor.competitorResults?.url || selectedCompetitor.competitorResults?.targetUrl || selectedCompetitor.competitorUrl}</div>
                  </div>
                </div>
                <ComparisonSections ourResults={selectedCompetitor.ourResults} competitorResults={selectedCompetitor.competitorResults} />
              </div>
              </>
            );
          })()}

        {/* Add Competitor Modal */}
        {showAddModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">Add Competitor</h3>
                <button className="modal-close" onClick={handleCloseModal}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <form onSubmit={handleAnalyze} className="modal-form">
                <div className="form-group">
                  <label htmlFor="modal-target-url">Your Target URL</label>
                  <input
                    type="text"
                    id="modal-target-url"
                    value={targetUrl}
                    onChange={(e) => setTargetUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="url-input"
                    disabled={loading}
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="modal-competitor-url">Competitor URL</label>
                  <input
                    type="text"
                    id="modal-competitor-url"
                    value={competitorUrl}
                    onChange={(e) => setCompetitorUrl(e.target.value)}
                    placeholder="https://competitor.com"
                    className="url-input"
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="modal-keyword">Keyword (Optional)</label>
                  <input
                    type="text"
                    id="modal-keyword"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="Enter keyword to analyze"
                    className="keyword-input"
                    disabled={loading}
                  />
                </div>
                {error && (
                  <div className="error-message">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <span>{error}</span>
                  </div>
                )}
                <div className="modal-actions">
                  <button
                    type="button"
                    className="modal-cancel-btn"
                    onClick={handleCloseModal}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="modal-submit-btn" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner"></span>
                        Analyzing...
                      </>
                    ) : (
                      'Analyze'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={(e) => {
            // Only close if clicking directly on overlay, not on modal content
            if (e.target === e.currentTarget) {
              handleCancelDelete();
            }
          }}>
            <div className="modal-content confirm-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="confirm-dialog-header">
                <h3 className="confirm-dialog-title">Delete Competitor</h3>
                <button className="modal-close" onClick={handleCancelDelete}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div className="confirm-dialog-body">
                <p className="confirm-dialog-message">
                  Are you sure you want to delete the competitor <strong>"{deletingCompetitorName}"</strong>?
                </p>
                <p className="confirm-dialog-warning">
                  This action cannot be undone.
                </p>
              </div>
              <div className="confirm-dialog-actions">
                <button
                  type="button"
                  className="confirm-dialog-cancel-btn"
                  onClick={handleCancelDelete}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="confirm-dialog-delete-btn"
                  onClick={handleConfirmDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
        )}

        {/* Upgrade Modal */}
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          onUpgrade={() => {
            setShowUpgradeModal(false);
            window.open('https://parrotseo.com/pricing', '_blank', 'noopener,noreferrer');
          }}
        />
      </div>
    </div>
  );
};

export default CompetitorAnalysis;
